import React, { useEffect, useState } from "react"
import { Button, Col, Form, Row, Table } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { Formik } from "formik"
import _ from "lodash"

import {
  getFieldUpdateContext,
  getFieldValues,
  getProductId,
} from "@/onboarding/features/product/selectors"
import { updateFields } from "@/onboarding/features/product/Slice"
import LoadingSpinner from "@/components/LoadingSpinner"
import { useProductSubmitting } from "@/onboarding/features/product/helpers"
import Api from "@/api"
import AsyncSearch, { DataStates } from "@/helpers/AsyncSearch"
import { deepSnakeizeKeys, isBlank } from "@/utils"
import Pricing from "@/pricing"
import { newAxiosErrorHandler } from "@/osn"
import Routes from "@/routes"
import TextDisplay from "@/onboarding/features/product/components/shared/TextDisplay"
import { fieldLabel } from "@/onboarding/features/product/models/fieldLabels"

const SearchSection = ({ selectedValue, onSearchSelect, fieldNames }): JSX.Element => {
  const context = useSelector(getFieldUpdateContext)
  // AsyncSearch leaves data-state tracking to the caller: without these props
  // its placeholder reads "Loading..." forever, even after options arrive.
  const [dataState, setDataState] = useState(DataStates.LOADING)
  const [searchMeta, setSearchMeta] = useState(null)
  const normalizedSelectedValues = _([selectedValue]).compact().value()
  // In bid context the source list spans every tour where the operator holds
  // a bid; product (cruise line) context stays shared-code.
  const bidContext = !isBlank(context.bidId)
  const truncated = searchMeta && searchMeta.totalCount > searchMeta.shown

  return (
    <Row>
      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>
            Source Tour
            <i
              title="Search tours for a set of importable values and display a preview of the changes. Saving will replace the listed field values of the current tour with the imported tour values."
              className="fas fa-info-circle d-inline ms-2 cursor-pointer"
            />
          </Form.Label>
          <AsyncSearch
            selectedValues={normalizedSelectedValues}
            setSelectedValues={onSearchSelect}
            getOptions={(query) =>
              // The section's field set rides along so the operator-context
              // search can withhold split-appointment sources from
              // component-bearing sections.
              Api.onboardingProduct.searchImportableProducts(context, query, fieldNames)
            }
            onOptionsLoaded={(state, meta) => {
              setDataState(state)
              setSearchMeta(meta)
            }}
            dataState={dataState}
            absentDataMessage={
              bidContext ? "You hold no bids on other tours." : "No tours share this tour code."
            }
            maxSelectable={1}
          />
          <Form.Text muted>
            {bidContext
              ? truncated
                ? `Showing ${searchMeta.shown} of ${searchMeta.totalCount} tours — typing a tour name, code or port searches all ${searchMeta.totalCount}.`
                : "Tours where you hold a bid — this tour's code and port rank first."
              : "Tour ideas and tours sharing this tour code."}
          </Form.Text>
        </Form.Group>
      </Col>
    </Row>
  )
}

const buildComparisonTableData = (oldValues, newValues) => {
  const fieldNames = _.keys(newValues)

  return fieldNames.map((fieldName) => ({
    fieldName,
    oldValue: oldValues[fieldName],
    newValue: newValues[fieldName],
  }))
}

export const FieldDisplay = ({ value }): JSX.Element => {
  // Booleans first: lodash isEmpty (inside isBlank) is true for both true and false.
  if (_.isBoolean(value)) {
    return <span>{value ? "Yes" : "No"}</span>
  }

  if (isBlank(value)) {
    return <i>Blank</i>
  }

  if (_.isString(value) || _.isNumber(value)) {
    return <span>{value}</span>
  }

  // Referenced-value selections ({ multiple, value: { id, label } }, or a
  // list of such options when multiple) render by label.
  if (!_.isUndefined(value.multiple) && !_.isUndefined(value.value)) {
    const labels = _.castArray(value.value ?? [])
      .map((item) => item?.label)
      .filter((label) => !isBlank(label))

    return labels.length ? <span>{labels.join(", ")}</span> : <i>Blank</i>
  }

  if (!isBlank(value.kind) && _.startsWith(value.kind, "Pricing")) {
    return <Pricing.View value={value} />
  }

  if (!isBlank(value.ratesheet)) {
    return <Pricing.View value={value.ratesheet} />
  }

  // File-backed component values (media image_src, attachment file_src)
  // carry a file reference under a key ending in "_src" — the same generic
  // marker preserve_files keys on. (Restaurant/refreshment menu_src never
  // reaches here: their schemas require a ratesheet, so the branch above
  // always intercepts them first.) Text-first (users are often on unstable
  // ship wifi): no thumbnails, just the meaningful scalar fields and a
  // plain download link.
  if (_.isPlainObject(value)) {
    const fileSrcKey = _.keys(value).find((key) => _.endsWith(key, "_src"))

    if (fileSrcKey) {
      const fileId = value[fileSrcKey]

      return (
        <div className="d-flex flex-column gap-1">
          <span>
            <b>{_.startCase(value.kind) || "File"}:</b> <TextDisplay value={value.name} />
          </span>
          {!isBlank(value.description) && <span>{value.description}</span>}
          {value.promotional && <span>(Promotional)</span>}
          {isBlank(fileId) ? (
            <span className="text-danger">No file</span>
          ) : (
            <a href={Routes.file_holder_path({ id: fileId })} download className="text-underline">
              Download
            </a>
          )}
        </div>
      )
    }
  }

  if (_.isArray(value)) {
    if (value.every((item) => !_.isObject(item))) {
      return (
        <div className="d-flex flex-column gap-1">
          {value.map((item, index) => (
            <span key={index}>{item}</span>
          ))}
        </div>
      )
    }

    return (
      <div className="d-flex flex-column gap-1">
        {value.map((item, index) => (
          <Row key={index}>
            <Col md={6}>
              <Table>
                <thead>
                  <tr>
                    <th>Component Name</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{item.name || "None specified"}</td>
                  </tr>
                </tbody>
              </Table>
            </Col>
            <Col md={6}>
              <FieldDisplay value={item} />
            </Col>
          </Row>
        ))}
      </div>
    )
  }

  return null
}

const FieldPreview = ({ setImporting, currentValues, previewValues }): JSX.Element => {
  const dispatch = useDispatch()
  const [isSubmitting, setSubmitting] = useProductSubmitting()
  const [submitted, setSubmitted] = useState(false)

  // Return to the section view once the import round-trip settles, instead of
  // leaving a stale diff on screen.
  useEffect(() => {
    if (submitted && !isSubmitting) {
      setImporting(false)
    }
  }, [submitted, isSubmitting])

  if (_.isEmpty(previewValues)) {
    return (
      <div className="d-flex flex-column gap-3">
        <Row>
          <Col>
            <p className="mb-0">No difference in importable values. Please select another tour.</p>
          </Col>
        </Row>
        <div>
          <Button size="sm" variant="outline-danger" onClick={() => setImporting(false)}>
            <i className="fas fa-times me-2" />
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  return (
    <Formik
      initialValues={previewValues}
      onSubmit={(values) => {
        setSubmitted(true)
        setSubmitting(true)
        dispatch(updateFields({ values }))
      }}
    >
      {({ handleSubmit, values }) => (
        <Form className="d-flex flex-column gap-3" onSubmit={handleSubmit}>
          <Row>
            <Table className="import-diff-table">
              <thead>
                <tr>
                  <th>Field Name</th>
                  <th>Current Tour Values</th>
                  <th>Importable Tour Values</th>
                </tr>
              </thead>
              <tbody>
                {_.map(buildComparisonTableData(currentValues, values), (data) => (
                  <tr key={data.fieldName}>
                    <td>{fieldLabel(data.fieldName)}</td>
                    <td>
                      <FieldDisplay value={data.oldValue} />
                    </td>
                    <td>
                      <FieldDisplay value={data.newValue} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Row>
          <div className="d-flex gap-2">
            <Button size="sm" type="submit" disabled={isSubmitting}>
              <i className="fas fa-save me-2" />
              Import and Replace
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => setImporting(false)}
              disabled={isSubmitting}
            >
              <i className="fas fa-times me-2" />
              Cancel
            </Button>
            {isSubmitting && <LoadingSpinner />}
          </div>
        </Form>
      )}
    </Formik>
  )
}

const Importer = ({ setImporting, fieldNames }): JSX.Element => {
  const dispatch = useDispatch()
  const productId = useSelector(getProductId)
  const context = useSelector(getFieldUpdateContext)
  const currentValues = useSelector(getFieldValues(...fieldNames))
  const [previewProductContext, setPreviewProductContext] = useState(null)
  const [previewValues, setPreviewValues] = useState(null)

  const productSelected = !isBlank(previewProductContext)
  const previewLoaded = productSelected && !_.isNull(previewValues)

  const onSearchSelect = (results) => setPreviewProductContext(_.head(results))

  useEffect(() => {
    if (productSelected) {
      Api.onboardingProduct
        .fieldImportDiff(productId, fieldNames, context, previewProductContext.value)
        .then(({ data }) => setPreviewValues(deepSnakeizeKeys(data.data.diffValues)))
        .catch(newAxiosErrorHandler(dispatch))
    }
  }, [previewProductContext])

  return (
    <div className="d-flex flex-column gap-3">
      <SearchSection
        selectedValue={previewProductContext}
        onSearchSelect={onSearchSelect}
        fieldNames={fieldNames}
      />
      {previewLoaded ? (
        <FieldPreview
          setImporting={setImporting}
          currentValues={currentValues}
          previewValues={previewValues}
        />
      ) : (
        <div>
          <Button size="sm" variant="outline-danger" onClick={() => setImporting(false)}>
            <i className="fas fa-times me-2" />
            Cancel
          </Button>
        </div>
      )}
    </div>
  )
}

export default Importer
